import {
  Box,
  Flex,
  Icon,
  IconProps,
  Link,
  Separator,
  styled,
  Text,
  Tooltip,
} from '@driftt/dds'
import { faThumbsDown as downLight } from '@driftt/dds.icons/light/faThumbsDown'
import { faThumbsUp as upLight } from '@driftt/dds.icons/light/faThumbsUp'
import { faQuoteRight } from '@driftt/dds.icons/solid/faQuoteRight'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'
import { faThumbsDown as downSolid } from '@driftt/dds.icons/solid/faThumbsDown'
import { faThumbsUp as upSolid } from '@driftt/dds.icons/solid/faThumbsUp'
import { TypeFormats } from '@driftt/dds.reports'
import React, { useEffect, useMemo, useState } from 'react'

import {
  deleteAIScenarioReview,
  getAIScenarioReview,
  postAIScenarioReview,
  Thumbs,
  THUMBS_DOWN,
  THUMBS_UP,
  TMessageReview,
  useConvoReviewerData,
} from 'api/conversationReviews'
import { ScenarioExecutionsData } from 'hooks/useScenariosTriggeringPhrases'

const RowContainer = styled(Box, {
  width: '100%',
  padding: '2px',
})

const BaseContainer = styled(Flex, {
  borderRadius: '4px',
  padding: '$2',
  marginTop: '$2',
})

const PurpleContainer = styled(BaseContainer, {
  background: '$purple1',
  border: '1px solid $purple4',
})

const BlueContainer = styled(BaseContainer, {
  background: '$blue1',
  border: '1px solid $blue4',
})

const THUMBS_ICONS: Record<
  string,
  Record<string, { icon: IconProps['icon']; color: IconProps['color'] }>
> = Object.freeze({
  [THUMBS_UP]: {
    selected: {
      icon: upSolid,
      color: 'purple8',
    },
    regular: {
      icon: upLight,
      color: 'gray12',
    },
    disabled: {
      icon: upLight,
      color: 'gray7',
    },
  },
  [THUMBS_DOWN]: {
    selected: {
      icon: downSolid,
      color: 'purple8',
    },
    regular: {
      icon: downLight,
      color: 'gray12',
    },
    disabled: {
      icon: downLight,
      color: 'gray7',
    },
  },
})

const Phrase = ({
  phraseData,
  divider = false,
}: {
  phraseData: ScenarioExecutionsData
  divider?: boolean
  isAIGenerated?: boolean
}) => {
  const [review, setReview] = useState<TMessageReview | undefined>(undefined)

  const { data: userData } = useConvoReviewerData(review?.createdBy)

  const displayName = useMemo(() => {
    const userDataObj = userData?.data
    return userDataObj?.name ?? userDataObj?.alias ?? userDataObj?.email
  }, [userData])

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const review = await getAIScenarioReview(
          phraseData?.conversationId,
          phraseData?.triggeringMessageId
        )
        setReview(review)
      } catch (error) {
        console.error('Error fetching review:', error)
      }
    }
    fetchReview()
  }, [phraseData])

  const handleThumbClick = async (thumbs: Thumbs) => {
    try {
      if (review) {
        await deleteAIScenarioReview(
          phraseData?.conversationId,
          phraseData?.triggeringMessageId
        )
      } else {
        await postAIScenarioReview(
          phraseData?.conversationId,
          phraseData?.triggeringMessageId,
          thumbs,
          phraseData?.scenarioId,
          phraseData?.scenarioName,
          phraseData?.scenarioType === 'CUSTOM'
            ? 'CUSTOM_SCENARIO'
            : 'GLOBAL_SCENARIO'
        )
      }
      setReview(
        await getAIScenarioReview(
          phraseData?.conversationId,
          phraseData?.triggeringMessageId
        )
      )
    } catch (error) {
      console.error('Error posting review:', error)
    }
  }

  const thumbs = review?.thumbs

  return (
    <RowContainer>
      <Flex direction="row" justifyContent="space-between">
        <Link
          href={`/conversations/${phraseData?.conversationId}`}
          variant="inline"
          css={{ cursor: 'pointer' }}
        >
          <Text
            css={{
              color: '$blue8',
              overflowWrap: 'break-word',
              whiteSpace: 'initial',
            }}
          >
            <Icon
              color="gray4"
              icon={faQuoteRight}
              css={{ marginRight: '$1' }}
            />
            {phraseData?.scenarioMessage}
          </Text>
        </Link>
        <Flex>
          <Tooltip
            content={
              review?.thumbs === THUMBS_UP
                ? `Reviewed by ${displayName} on ${TypeFormats.DATE(
                    review.createdAt
                  )}`
                : `Correctly triggered ${phraseData?.scenarioName} scenario`
            }
          >
            <Icon
              icon={
                THUMBS_ICONS[THUMBS_UP][
                  thumbs === THUMBS_UP ? 'selected' : 'regular'
                ].icon
              }
              color={
                THUMBS_ICONS[THUMBS_UP][
                  thumbs === THUMBS_UP ? 'selected' : 'regular'
                ].color
              }
              css={{ cursor: 'pointer' }}
              onClick={() => handleThumbClick(THUMBS_UP)}
            />
          </Tooltip>
          <Tooltip
            content={
              review?.thumbs === THUMBS_DOWN
                ? `Reviewed by ${displayName} on ${TypeFormats.DATE(
                    review.createdAt
                  )}`
                : `Should not have triggered ${phraseData?.scenarioName} scenario`
            }
          >
            <Icon
              icon={
                THUMBS_ICONS[THUMBS_DOWN][
                  thumbs === THUMBS_DOWN ? 'selected' : 'regular'
                ].icon
              }
              color={
                THUMBS_ICONS[THUMBS_DOWN][
                  thumbs === THUMBS_DOWN ? 'selected' : 'regular'
                ].color
              }
              css={{ cursor: 'pointer' }}
              onClick={() => handleThumbClick(THUMBS_DOWN)}
            />
          </Tooltip>
        </Flex>
      </Flex>
      {divider && <Separator />}
    </RowContainer>
  )
}

const StaticInput = ({
  phrases,
  aiGeneratedPhrases,
  isAIGenerated,
}: {
  phrases: ScenarioExecutionsData[]
  aiGeneratedPhrases: string[]
  isAIGenerated?: boolean
}) => {
  const Container = isAIGenerated ? PurpleContainer : BlueContainer
  return (
    <Container direction="column" gap={1} alignItems="stretch">
      {isAIGenerated ? (
        <Text variant="bodyBold">
          <Icon color="purple7" icon={faSparkles} css={{ marginRight: '$1' }} />
          Sample phrases that would trigger this scenario
        </Text>
      ) : (
        <Text variant="bodyBold">
          Site visitor messages that have triggered this scenario
        </Text>
      )}
      <Flex
        direction="column"
        gap={0}
        css={{ maxHeight: '150px', overflowY: 'auto' }}
      >
        {isAIGenerated
          ? aiGeneratedPhrases.map((phrase, i) => (
              <RowContainer css={{ borderTop: '1px solid $gray3' }}>
                <Text css={{ color: isAIGenerated ? '$primary' : '$blue8' }}>
                  <Icon
                    color="gray4"
                    icon={faQuoteRight}
                    css={{ marginRight: '$1' }}
                  />
                  {phrase}
                </Text>
              </RowContainer>
            ))
          : phrases.map((phraseData, i) => (
              <Phrase key={i} phraseData={phraseData} divider={i !== -1} />
            ))}
      </Flex>
    </Container>
  )
}

export default StaticInput
